import Image from 'next/image';
import { formatTimeUntilClaim } from "@/types/rewards";

interface BonusCardProps {
    title: string;
    description: string;
    imageSrc: string;
    canClaim: boolean;
    isCollecting: boolean;
    progress: number;
    bonusAmount: number | null;
    secondsUntilClaim: number;
    onCollect: () => void;
    colorScheme: {
        gradient: string;
        progressBar: string;
        button: string;
        successBg: string;
        successText: string;
    };
}

export function BonusCard({
    title,
    description,
    imageSrc,
    canClaim,
    isCollecting,
    bonusAmount,
    secondsUntilClaim,
    onCollect,
    colorScheme,
}: BonusCardProps) {
    const getButtonText = () => {
        if (bonusAmount !== null) {
            if (bonusAmount > 0) {
                return `+${bonusAmount} Kick Points`;
            }
            return 'Better luck next time!';
        }
        if (!canClaim) {
            return `Available in ${formatTimeUntilClaim(secondsUntilClaim)}`;
        }
        return 'COLLECT';
    };

    const getButtonStyle = () => {
        if (bonusAmount !== null) {
            if (bonusAmount > 0) {
                return `${colorScheme.successBg} ${colorScheme.successText}`;
            }
            return 'bg-gray-700/50 text-gray-300';
        }
        if (!canClaim) {
            return 'bg-gray-800 text-gray-400 cursor-not-allowed';
        }
        return `bg-gradient-to-r ${colorScheme.button} text-white hover:shadow-lg`;
    };

    return (
        <div className={`relative bg-gradient-to-br ${colorScheme.gradient} to-gray-900 p-6 rounded-xl shadow-xl`}>
            <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-20 h-20 mb-3">
                    <Image
                        src={imageSrc}
                        alt={title}
                        width={250}
                        height={250}
                        className="pointer-events-none select-none max-w-full max-h-full object-contain"
                    />
                </div>
                <h3 className="font-bold text-xl text-white mb-1">{title}</h3>
                <p className="text-xs text-gray-300">{description}</p>
            </div>

            <button
                onClick={onCollect}
                disabled={isCollecting || !canClaim || bonusAmount !== null}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all ${getButtonStyle()}`}
            >
                {getButtonText()}
            </button>
        </div>
    );
}